import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.ts'

/** Campos complementares (SPEC-20260828-1914) — hooks de dados do admin. */

export type CustomFieldType =
  | 'text'
  | 'textarea'
  | 'richtext'
  | 'link'
  | 'image'
  | 'file'
  | 'button'
  | 'number'
  | 'date'
  | 'datetime'
  | 'boolean'
  | 'select'
  | 'multiselect'

export type CustomField = {
  id: string
  entity: string
  workItemTypeId: string | null
  groupId: string | null
  /** Pasta do CATALOGO (um nivel); null = solto na estante. */
  catalogGroupId: string | null
  key: string
  labels: Record<string, string>
  type: CustomFieldType
  required: boolean
  defaultVisible: boolean
  sortOrder: number
  config: { options?: { value: string; label: string }[]; [k: string]: unknown }
}

export type CustomFieldGroup = {
  id: string
  entity: string
  workItemTypeId: string | null
  kind: 'topic' | 'catalog'
  labels: Record<string, string>
  sortOrder: number
  config: Record<string, unknown>
}

/** Tipo de visualização de um tópico de campos complementares. */
export type ViewType = 'simple' | 'tab' | 'form'

/** Lê o viewType do config do tópico (default 'simple'). */
export function viewTypeDe(config: Record<string, unknown> | null | undefined): ViewType {
  const v = config?.['viewType']
  return v === 'tab' || v === 'form' ? v : 'simple'
}

/** Onde um campo aparece. Um campo pode ter várias colocações. */
export type CustomFieldPlacement = {
  id: string
  fieldId: string
  groupId: string | null
  sortOrder: number
  /** Largura no grid de 12 colunas (1-12). 12 = linha inteira; 6 = meia; 4 = terço. */
  width: number
  /** Config por colocação: { visibilityRule, requiredRule, ... } (fase 2). */
  config: { visibilityRule?: unknown; requiredRule?: unknown; [k: string]: unknown }
}

const CHAVE = (entity: string, typeId?: string) => ['custom-fields', entity, typeId ?? 'all'] as const

async function corpo<T>(r: Response): Promise<T> {
  const d = (await r.json()) as T & { error?: { message?: string } }
  if (!r.ok) throw new Error(d.error?.message ?? 'Não foi possível concluir a operação.')
  return d
}

type Entity = 'work_item' | 'company' | 'person'

export function useCustomFields(entity: Entity, typeId?: string) {
  return useQuery({
    queryKey: CHAVE(entity, typeId),
    queryFn: async () => {
      const r = await api['custom-fields'].$get({ query: { entity, ...(typeId ? { typeId } : {}) } })
      return corpo<{ fields: CustomField[]; groups: CustomFieldGroup[]; placements: CustomFieldPlacement[] }>(r)
    },
  })
}

/** Catálogo de campos das 3 entidades (para o painel de arrastar). */
export function useCatalog() {
  return useQuery({
    queryKey: ['custom-fields', 'catalog'],
    queryFn: async () => {
      const r = await api['custom-fields'].catalog.$get()
      return corpo<{
        work_item: CustomField[]
        company: CustomField[]
        person: CustomField[]
        /** Pastas do catálogo (kind = 'catalog'), de todas as entidades. */
        folders: CustomFieldGroup[]
        /** Tópicos (kind = 'topic') — para dizer ONDE cada campo é usado. */
        topics: CustomFieldGroup[]
        placements: CustomFieldPlacement[]
      }>(r)
    },
  })
}

export function useAddPlacement(entity: string) {
  const invalida = useInvalida(entity)
  return useMutation({
    mutationFn: async (input: { fieldId: string; groupId: string | null }) => {
      const r = await api['custom-fields'].placements.$post({ json: input as never })
      return corpo<{ placement: CustomFieldPlacement }>(r)
    },
    onSuccess: invalida,
  })
}

export function useRemovePlacement(entity: string) {
  const invalida = useInvalida(entity)
  return useMutation({
    mutationFn: async (id: string) => {
      await corpo<{ ok: true }>(await api['custom-fields'].placements[':id'].$delete({ param: { id } }))
      return id
    },
    onSuccess: invalida,
  })
}

/** Salva a config (regras) de UMA colocação. */
export function useSavePlacement(entity: string) {
  const invalida = useInvalida(entity)
  return useMutation({
    mutationFn: async (input: { id: string; config: Record<string, unknown> }) => {
      const r = await api['custom-fields'].placements[':id'].$patch({
        param: { id: input.id },
        json: { config: input.config } as never,
      })
      return corpo<{ placement: CustomFieldPlacement }>(r)
    },
    onSuccess: invalida,
  })
}

/**
 * Salva ordem + largura de várias colocações de uma vez (drag/resize do layout).
 *
 * A nova posição entra no cache ANTES da resposta: sem isso a tela só se
 * arrumava depois da gravação e do refetch, e o campo parecia voltar para o
 * lugar antigo por um instante longo.
 */
export function useSaveLayout(entity: string) {
  const invalida = useInvalida(entity)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (items: { id: string; sortOrder: number; width: number }[]) => {
      const r = await api['custom-fields'].placements.layout.$patch({ json: { items } as never })
      return corpo<{ ok: true; count: number }>(r)
    },
    onMutate: (items) => {
      const novo = new Map(items.map((i) => [i.id, i]))
      qc.setQueriesData<{ placements?: CustomFieldPlacement[] }>({ queryKey: ['custom-fields'] }, (atual) => {
        if (!atual?.placements) return atual
        return {
          ...atual,
          placements: atual.placements.map((p) => {
            const mudou = novo.get(p.id)
            return mudou ? { ...p, sortOrder: mudou.sortOrder, width: mudou.width } : p
          }),
        }
      })
    },
    onSuccess: invalida,
  })
}

function useInvalida(_entity: string) {
  const qc = useQueryClient()
  // invalida tudo de custom-fields (listas por entidade, catálogo e o detalhe
  // do item) — assim o painel de catálogo e as abas refletem qualquer mudança.
  return () => qc.invalidateQueries({ queryKey: ['custom-fields'] })
}

export function useSaveField(entity: string) {
  const invalida = useInvalida(entity)
  return useMutation({
    mutationFn: async (input: Partial<CustomField> & { id?: string }) => {
      const { id, ...corpoReq } = input
      const r = id
        ? await api['custom-fields'][':id'].$patch({ param: { id }, json: corpoReq as never })
        : await api['custom-fields'].$post({ json: corpoReq as never })
      return corpo<{ field: CustomField }>(r)
    },
    onSuccess: invalida,
  })
}

export function useDeleteField(entity: string) {
  const invalida = useInvalida(entity)
  return useMutation({
    mutationFn: async (id: string) => {
      await corpo<{ ok: true }>(await api['custom-fields'][':id'].$delete({ param: { id } }))
      return id
    },
    onSuccess: invalida,
  })
}

/** Prévia de impacto do delete: quantos itens têm valor no campo. */
export async function fieldImpact(id: string) {
  const r = await api['custom-fields'][':id'].impact.$get({ param: { id } })
  return corpo<{ key: string; entity: string; itemsComValor: number }>(r)
}

export function useSaveGroup(entity: string) {
  const invalida = useInvalida(entity)
  return useMutation({
    mutationFn: async (input: Partial<CustomFieldGroup> & { id?: string }) => {
      const { id, ...corpoReq } = input
      const r = id
        ? await api['custom-fields'].groups[':id'].$patch({ param: { id }, json: corpoReq as never })
        : await api['custom-fields'].groups.$post({ json: corpoReq as never })
      return corpo<{ group: CustomFieldGroup }>(r)
    },
    onSuccess: invalida,
  })
}

export function useDeleteGroup(entity: string) {
  const invalida = useInvalida(entity)
  return useMutation({
    mutationFn: async (id: string) => {
      await corpo<{ ok: true }>(await api['custom-fields'].groups[':id'].$delete({ param: { id } }))
      return id
    },
    onSuccess: invalida,
  })
}

export const TIPOS_CAMPO: { value: CustomFieldType; label: string; icon: string; hint: string }[] = [
  { value: 'text', label: 'Texto curto', icon: 'fas fa-font', hint: 'Uma linha, com máscara opcional' },
  { value: 'textarea', label: 'Texto longo', icon: 'fas fa-align-left', hint: 'Várias linhas, sem formatação' },
  { value: 'richtext', label: 'Parágrafo', icon: 'fas fa-paragraph', hint: 'Editor completo, igual ao da descrição' },
  { value: 'link', label: 'Link', icon: 'fas fa-link', hint: 'Endereço que abre a partir do campo' },
  { value: 'image', label: 'Imagem', icon: 'far fa-image', hint: 'Envio com visualização e zoom' },
  { value: 'file', label: 'Anexo', icon: 'fas fa-paperclip', hint: 'Arquivo guardado no cofre, abre ao clicar' },
  { value: 'button', label: 'Botão', icon: 'fas fa-external-link-alt', hint: 'Abre um endereço configurado' },
  { value: 'number', label: 'Número', icon: 'fas fa-hashtag', hint: 'Valor numérico' },
  { value: 'date', label: 'Data', icon: 'far fa-calendar', hint: 'Só o dia' },
  { value: 'datetime', label: 'Data e hora', icon: 'far fa-clock', hint: 'Dia e horário' },
  { value: 'boolean', label: 'Sim/Não', icon: 'fas fa-check-square', hint: 'Caixa de seleção' },
  { value: 'select', label: 'Seleção única', icon: 'fas fa-caret-square-down', hint: 'Menu suspenso' },
  { value: 'multiselect', label: 'Múltipla escolha', icon: 'fas fa-list-ul', hint: 'Marca várias' },
]
